import React from "react";
import { useLang } from "../contexts/LanguageContext";
import { Sprout, Droplets, Globe2 } from "lucide-react";

const FACTORY_IMG =
  "https://images.unsplash.com/photo-1716643863806-989dd76ae093?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtYW51ZmFjdHVyaW5nJTIwZmFjaWxpdHklMjBmYWN0b3J5JTIwY2xlYW58ZW58MHx8fHwxNzc3MTMyMTI3fDA&ixlib=rb-4.1.0&q=85";
const FIELD_IMG =
  "https://images.unsplash.com/photo-1566183667136-3c56b0fa9a88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxzdW5mbG93ZXIlMjBmaWVsZCUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzc3MTMyMTI3fDA&ixlib=rb-4.1.0&q=85";

const About = () => {
  const { t } = useLang();
  const Icon = ({ i }) => {
    const props = { size: 18, strokeWidth: 1.5 };
    if (i === 0) return <Sprout {...props} />;
    if (i === 1) return <Droplets {...props} />;
    return <Globe2 {...props} />;
  };
  const pillars = [t.about.pillar1, t.about.pillar2, t.about.pillar3];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[#F3EFE7] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left text column */}
          <div className="lg:col-span-5 lg:pt-12">
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="divider-line" />
              <span className="overline text-[#DE9E36]">{t.about.overline}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl text-[#1A201C] font-bold leading-[1.1] reveal tracking-tight">
              {t.about.title}
            </h2>
            <p className="mt-8 text-base text-[#4E5750] leading-relaxed reveal">{t.about.p1}</p>
            <p className="mt-4 text-base text-[#4E5750] leading-relaxed reveal">{t.about.p2}</p>

            {/* Pillars */}
            <div className="mt-12 space-y-6">
              {pillars.map((p, i) => (
                <div
                  key={i}
                  data-testid={`about-pillar-${i}`}
                  className="reveal flex items-start gap-5 p-5 border border-[#E5E0D8] bg-white/40 hover:bg-white/70 transition-colors"
                >
                  <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-[#1A201C] text-[#DE9E36]">
                    <Icon i={i} />
                  </span>
                  <div>
                    <div className="text-lg text-[#1A201C] font-semibold">{p.t}</div>
                    <div className="text-sm text-[#4E5750] mt-1 leading-relaxed">{p.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right asymmetric images */}
          <div className="lg:col-span-7 relative h-[640px] sm:h-[720px]">
            <div className="absolute top-0 right-0 w-[68%] aspect-[4/5] overflow-hidden reveal">
              <img
                src={FACTORY_IMG}
                alt="Modern manufacturing facility"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
            <div className="absolute bottom-0 left-0 w-[58%] aspect-[5/6] overflow-hidden reveal">
              <img
                src={FIELD_IMG}
                alt="Sunflower field"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
            {/* Quote card */}
            <div className="absolute bottom-12 right-4 sm:right-12 max-w-xs bg-[#1A201C] text-[#FDFBF7] p-7 reveal">
              <div className="text-5xl text-[#DE9E36] font-bold leading-none tracking-tight">2003</div>
              <div className="overline text-white/60 mt-3">EDRPOU 32125693</div>
              <div className="mt-4 text-sm leading-relaxed text-white/80">
                Kherson · Ukraine — 90 employees crafting oil since two decades.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
